import { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

/* Öffentliches Hilfe-Formular (29.09.2026) — Link in der Rundmail, QR-Code auf dem
   Aushang. Für alle, die (noch) nicht in die App kommen. Jede Meldung landet im
   CRM unter „Support & Umstieg“; die Antwort kommt per E-Mail. Bewusst
   eigenständig und hell, fürs Handy nach dem QR-Scan. */
const ROT = '#E60000';
const THEMEN = [
  ['anmeldung', 'Ich komme nicht in die App'],
  ['kind', 'Mein Kind fehlt in der App'],
  ['daten', 'Meine Daten stimmen nicht'],
  ['buchung', 'Buchung oder Check-in'],
  ['sonstiges', 'Etwas anderes'],
];

export default function HilfePage() {
  const von = new URLSearchParams(window.location.search).get('von') || '';
  const [f, setF] = useState({ vorname: '', nachname: '', email: '', telefon: '', thema: 'anmeldung', nachricht: '', datenschutz: false, website: '' });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState(false);
  const s = (k, v) => setF(x => ({ ...x, [k]: v }));

  const senden = async (e) => {
    e.preventDefault();
    setFehler('');
    if (!f.email.trim() && !f.telefon.trim()) { setFehler('Bitte E-Mail oder Handynummer angeben, damit wir antworten können.'); return; }
    if (!f.datenschutz) { setFehler('Bitte bestätige die Datenschutzhinweise.'); return; }
    setBusy(true);
    try { await api.post('/public/hilfe', { ...f, von }); setFertig(true); window.scrollTo(0, 0); }
    catch (err) { setFehler(err?.response?.data?.detail || 'Das hat nicht geklappt. Bitte versuch es gleich noch einmal oder schreib uns per WhatsApp.'); }
    setBusy(false);
  };

  const feld = 'w-full h-12 rounded-xl border border-gray-300 bg-white px-4 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E60000]/40';
  return (
    <div className="min-h-screen bg-[#EEECE9] text-gray-900" data-testid="hilfe-page">
      <header className="bg-[#0B0B0C] text-white px-5 pt-6 pb-8">
        <div className="max-w-md mx-auto">
          <img src="/logo.png" alt="P1 Performance Center" className="h-8 object-contain mb-5" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#EF7C22]">Hilfe zur P1 App</p>
          <h1 className="text-2xl font-extrabold leading-tight mt-1">Wo hakt es? Wir helfen dir.</h1>
          <p className="text-sm text-gray-300 mt-2">Schreib uns kurz, was nicht klappt – das Team am Empfang meldet sich per E-Mail oder Telefon.</p>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 -mt-4 pb-10">
        {fertig ? (
          <div className="rounded-2xl bg-white p-6 shadow-sm text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
            <p className="text-lg font-bold">Danke, ist angekommen!</p>
            <p className="text-sm text-gray-600">Wir kümmern uns darum und melden uns so schnell wie möglich – meist noch am selben Tag.</p>
            <p className="text-sm text-gray-600">Eilig? Schreib uns per WhatsApp: <a className="font-semibold underline" href="https://wa.me/4915560882292">0155 60882292</a></p>
          </div>
        ) : (
          <form onSubmit={senden} className="rounded-2xl bg-white p-5 shadow-sm space-y-4" noValidate>
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold mb-1">Worum geht es?</legend>
              {THEMEN.map(([k, l]) => (
                <label key={k} className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-base cursor-pointer ${f.thema === k ? 'border-[#E60000] bg-[#E60000]/5' : 'border-gray-200'}`}>
                  <input type="radio" name="thema" value={k} checked={f.thema === k} onChange={() => s('thema', k)} className="w-5 h-5 accent-[#E60000]" />{l}
                </label>
              ))}
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1"><span className="text-sm font-medium">Vorname *</span><input className={feld} value={f.vorname} onChange={e => s('vorname', e.target.value)} autoComplete="given-name" required minLength={2} /></label>
              <label className="space-y-1"><span className="text-sm font-medium">Nachname</span><input className={feld} value={f.nachname} onChange={e => s('nachname', e.target.value)} autoComplete="family-name" /></label>
            </div>
            <label className="block space-y-1"><span className="text-sm font-medium">E-Mail</span><input className={feld} type="email" inputMode="email" value={f.email} onChange={e => s('email', e.target.value)} autoComplete="email" placeholder="wie bei uns hinterlegt" /></label>
            <label className="block space-y-1"><span className="text-sm font-medium">Handynummer</span><input className={feld} type="tel" inputMode="tel" value={f.telefon} onChange={e => s('telefon', e.target.value)} autoComplete="tel" /></label>
            <p className="text-xs text-gray-500 -mt-2">Mindestens eins von beiden, damit wir antworten können.</p>
            <label className="block space-y-1"><span className="text-sm font-medium">Was ist passiert? *</span>
              <textarea className={`${feld} h-28 py-3`} value={f.nachricht} onChange={e => s('nachricht', e.target.value)} required minLength={3} maxLength={2000}
                placeholder={f.thema === 'anmeldung' ? 'z. B. „E-Mail wird nicht gefunden“ oder „Der Code kommt nicht an“' : f.thema === 'kind' ? 'Name und Geburtsdatum deines Kindes' : ''} /></label>
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={e => s('website', e.target.value)} className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-3 text-sm"><input type="checkbox" checked={f.datenschutz} onChange={e => s('datenschutz', e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#E60000]" />
              <span>Ich bin einverstanden, dass das P1 Performance Center meine Angaben zur Bearbeitung meiner Anfrage verwendet. <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">Datenschutzhinweise</a></span></label>
            {fehler && <p className="text-sm text-red-600" role="alert">{fehler}</p>}
            <button type="submit" disabled={busy} className="w-full h-13 py-3.5 rounded-xl text-white text-base font-bold flex items-center justify-center gap-2" style={{ background: ROT }} data-testid="hilfe-senden">
              {busy && <Loader2 className="w-5 h-5 animate-spin" />} Anfrage senden
            </button>
            <p className="text-xs text-gray-500 text-center">Lieber direkt? WhatsApp <a className="underline" href="https://wa.me/4915560882292">0155 60882292</a> · am Empfang · info@p1-pforzheim.de</p>
          </form>
        )}
        <p className="text-[11px] text-gray-500 text-center mt-6">P1 Performance Center GmbH · <a href="/impressum" className="underline">Impressum</a> · <a href="/datenschutz" className="underline">Datenschutz</a></p>
      </main>
    </div>
  );
}
